/**
 * #1400 — what changed between two reports of the same game, for the panels'
 * brief cues.
 *
 * Rows: a player whose status changed, or a player newly listed by a team
 * that had already filed. Teams (the panel header): a team that filed after
 * showing Not Yet Submitted, or a team whose earlier row is gone (a removed
 * player or a cleared list) — a row that has left cannot carry its own cue.
 * A reason-only edit or a republication with the same statuses is no change.
 * Whether there is a previous report at all — first mount, a new game, a
 * report returning after retirement, a reconnect snapshot — is the caller's
 * baseline decision (`useInjuryRowChanges` in InjuryLadderPanels).
 *
 * Identity is team + player name, not `playerKey`: a row that becomes paired
 * between revisions gains a key, and keying on it would present an unchanged
 * player as newly listed. Two rows of one team with the same name are
 * ambiguous; that name is left out of every comparison (no row cue, no
 * removal) rather than matched by guess.
 */
import type { InjuryBlock, InjuryPlayer } from './injuries';

export function injuryRowId(player: Pick<InjuryPlayer, 'team' | 'player'>): string {
  return `${player.team}|${player.player.trim().toLowerCase()}`;
}

export interface InjuryChanges {
  /** Row ids (`injuryRowId`) to cue. */
  rows: Set<string>;
  /** Team codes whose header to cue. */
  teams: Set<string>;
}

/** Rows by id; `null` marks an id that more than one row shares. */
function byId(players: readonly InjuryPlayer[]): Map<string, InjuryPlayer | null> {
  const out = new Map<string, InjuryPlayer | null>();
  for (const p of players) {
    const id = injuryRowId(p);
    out.set(id, out.has(id) ? null : p);
  }
  return out;
}

export function injuryChanges(prev: InjuryBlock, next: InjuryBlock): InjuryChanges {
  const rows = new Set<string>();
  const teams = new Set<string>();
  for (const [code, team] of Object.entries(next.teams)) {
    const before = prev.teams[code];
    if (!before) continue;
    if (!before.filed) {
      if (team.filed) teams.add(code);
      continue;
    }
    const old = byId(before.players);
    const now = byId(team.players);
    for (const [id, p] of now) {
      const was = old.get(id);
      if (p === null || was === null) continue;
      if (!was || was.status !== p.status || was.statusRaw !== p.statusRaw) rows.add(id);
    }
    for (const [id, was] of old) {
      if (was !== null && !now.has(id)) teams.add(code);
    }
  }
  return { rows, teams };
}
